import { useOnClickOutside } from "@qastanfit/qastan-ui";
import { format, isValid } from "date-fns";
import styles from "../styles/pages/Dossiers.module.scss"
import folder from "../assets/custom-icons/folder.svg";

import empty from "../assets/images/empty.svg";
import search from "../assets/icons/search.svg";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Popup from "../components/Popup";
import Warning from "../components/Warning";
import SearchDossiers from "../components/dossiers/SearchDossiers";
import { useAppSelector } from "../hooks/redux";
import { RootState } from "../store";
import { getItemById } from "../functions/convert";
import { filterObjectsByValue } from "../functions/general/filter";

import { Input } from "../components/ui/input";
import { YYYYMMDDtoDate } from "../functions/format";
import { ScrollArea, ScrollBar } from "src/components/ui/scroll-area";
import { ChevronsUpDownIcon, MoreVertical, Search, Trash2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from "src/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/components/ui/tooltip";
import useAuth from "src/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { getWindevLang } from "src/lib/lang";
import useTable from "src/hooks/useTable";

const Dossiers : React.FC = () => {
    const {t, i18n} = useTranslation()
    const {auth} = useAuth()
    const colleagues:ForwardUser[] = useAppSelector((state:RootState) => state.colleagues);
    const dossiersRedux:DossierObject[] = useAppSelector((state:RootState) => state.dossiers);
    const dossiertypes:DossiertypeObject[] = useAppSelector((state:RootState) => state.dossiertypes);
    const metatypeList:MetatypeObject[] = useAppSelector((state:RootState) => state.metatypes);

    const navigate = useNavigate();
    const [selectedView, setselectedView] = useState<"list" | "grid">("list");
    const [show_delete, setshow_delete] = useState(false);
    const [show_search, setshow_search] = useState(false);

    const [previous_opened_dossiers, setprevious_opened_dossiers] = useState<number[]>([]);
    const [dossiers, setdossiers] = useState<DossierObject[]>();

    useEffect(()=>{
        if(window.sessionStorage.getItem("d_view")){
            setselectedView(window.sessionStorage.getItem("d_view") as "list" | "grid")
        }
        let pod = window.localStorage.getItem("pod")
        if(pod){
            try {
                const tmp_pod = JSON.parse(pod)
                setprevious_opened_dossiers(tmp_pod)
            } catch (error) {}
        }
    }, [])

    useEffect(()=>{
        if(dossiersRedux && dossiersRedux.length > 0){
            setdossiers(dossiersRedux);
        }
    }, [dossiersRedux])

    const searchDossiers = (value:string) => {
        if(dossiers){
            let filtered = filterObjectsByValue(dossiersRedux, value);
            setdossiers(filtered as DossierObject[]);
        }
    }

    function usernameToName(name:string) {
        let n = name
        const user = Array.isArray(colleagues) ? colleagues.find(u=>u.username.toLowerCase() === name.toLowerCase()) : undefined
        if(user){
            n = `${user.firstname} ${user.name}`
        }
        return n
    }

    function handleOpenDossier(dossierid:number) {
        navigate(`${dossierid}`)
        try {
            const pod = window.localStorage.getItem('pod');
            let tmp_pod:any[] = []
            if(!pod){ tmp_pod.unshift(dossierid) }
            else {
                tmp_pod = JSON.parse(pod);
                if(tmp_pod.includes(dossierid)){
                    tmp_pod = tmp_pod.filter(id=>id!==dossierid)
                }
                tmp_pod.unshift(dossierid)
            }
            window.localStorage.setItem('pod', JSON.stringify(tmp_pod))
        } catch (error) {}
    }

    const getPreviousDossiers = () => {
        const dossiersToDisplay: any[] = [];

        if(!Array.isArray(dossiersRedux) || !dossiersRedux){ return dossiersToDisplay}
        if(!Array.isArray(previous_opened_dossiers)){ return dossiersToDisplay }

        // First, fill with dossier IDs
        previous_opened_dossiers.forEach((dossierId) => {
            if (dossiersToDisplay.length < 4) {
                const dossier = dossiersRedux.find((d) => d.dossierentityid === dossierId);
                if (dossier) {
                    dossiersToDisplay.push(dossier);
                }
            }
        });
        // Fill the remaining slots with dossiers from dossiersRedux
        dossiersRedux.forEach((dossier) => {
            if (dossiersToDisplay.length < 4 && !dossiersToDisplay.includes(dossier)) {
                dossiersToDisplay.push(dossier);
            }
        });

        return dossiersToDisplay.slice(0, 4); // Ensure maximum 4 dossiers
    };

    const getColumns = (results:DossierObject[]) => {
        if(!metatypeList){ return [] }
        if(!results){ return [] }
        let result:any[] = []
        let metatypeArray:string[] = []
        results.forEach(element => {
            if(element && element.metadata){
                return element.metadata.forEach(md => {
                    if(!metatypeArray.includes(md.metatype)){ metatypeArray.push(md.metatype) }
                })
            }
        });
        result = metatypeArray.filter(mt => {
            const item = metatypeList.find(i=>i.metatype===mt)
            return item?.columndisplay
        })
        return result
    }

    const {data:results, sorting, filter, updateFilter, updateSorting} = useTable(
        {
            data:dossiers || [], 
            sorting:{
                desc:false, 
                id:'dossierentityid'
            }
        }
    )
    
    return (
        <>
        {show_delete && 
        <Popup>
            <Warning onConfirm={()=>{}} onCancel={()=>{setshow_delete(false)}}/>
        </Popup>
        }

        {show_search &&
        <Popup>
            <SearchDossiers onClose={()=>{setshow_search(false)}}/>
        </Popup>
        }

        <div className={styles.dossiers}>
            <div className={styles.bg_image}></div>

            <div className={`${styles.content} p-4 md:p-16`}>
                <div className={styles.top_content}>
                    <h1 className='text-3xl font-bold text-white'>{t('Dossiers')}</h1>
                    <p className='text-sm text-white uppercase'>{t('Beheer hier al jouw FITdoc dossiers')}</p>
                </div>

                <div className='sticky top-0 z-10 flex flex-col gap-4 p-8 bg-white border rounded-lg shadow-sm'>
                    <h2 className='text-2xl font-semibold'>{t('Voorgesteld')}</h2>

                    <ScrollArea>
                    <div className='flex gap-3'>
                        {getPreviousDossiers().map((dossier, index) => {
                            return (
                                <Link to={`${dossier.dossierentityid}`} key={index}>
                                    <div className='border w-[250px] min-w-[250px] p-3 rounded-md group bg-white hover:bg-blue-100'>
                                        <div>
                                            <img className="w-10 h-10" src={folder} alt="folder" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-neutral-600 line-clamp-1 group-hover:text-blue-500">{dossier.extendeddescription || t('Geen omschrijving')}</p>
                                            <p className='text-xs text-neutral-400'>{format(YYYYMMDDtoDate(dossier.creationdate), "d MMMM yyyy")}</p>
                                        </div>
                                    </div>
                                </Link>
                            )
                        })}   
                    </div>
                    <ScrollBar orientation="horizontal"/>
                    </ScrollArea>
                </div>
                
                <div className={styles.result}>
                    <h2 className='text-2xl font-semibold'>{t('Alle dossiers')}</h2>
                    <div className={styles.options}>
                        <Input value={filter} onChange={({target}) => {updateFilter(target.value)}} startEnhancer={<Search className="w-4 h-4 text-neutral-400"/>} placeholder={`${t('Zoeken')}...`} className="w-fit"/>
                    </div>

                    {(Array.isArray(results) && results.length > 0) &&
                    <Table className="mt-4">
                        <TableHeader>
                            <TableRow>
                                <TableHead></TableHead>
                                <TableHead onClick={()=>{updateSorting({ id:'extendeddescription', desc:!sorting?.desc })}} className="cursor-pointer hover:underline">
                                    <div className="flex items-center gap-2">
                                        {t('Naam')}
                                        <ChevronsUpDownIcon className="w-4 h-4"/>
                                    </div>
                                </TableHead>
                                <TableHead>{t('Aantal documenten')}</TableHead>
                                <TableHead onClick={()=>{updateSorting({ id:'dossiertypeid', desc:!sorting?.desc })}} className="cursor-pointer hover:underline">
                                    <div className="flex items-center gap-2">
                                        {t('Type')}
                                        <ChevronsUpDownIcon className="w-4 h-4"/>
                                    </div>
                                </TableHead>
                                {/* <TableHead>Status</TableHead> */}
                                <TableHead onClick={()=>{updateSorting({ id:'owner', desc:!sorting?.desc })}} className="cursor-pointer hover:underline">
                                    <div className="flex items-center gap-2">
                                        {t('Eigenaar')}
                                        <ChevronsUpDownIcon className="w-4 h-4"/>
                                    </div>
                                </TableHead>
                                <TableHead onClick={()=>{updateSorting({ id:'creationdate', desc:!sorting?.desc })}} className="cursor-pointer hover:underline">
                                    <div className="flex items-center gap-2">
                                        {t('Datum aangemaakt')}
                                        <ChevronsUpDownIcon className="w-4 h-4"/>
                                    </div>
                                </TableHead>
                                <TableHead onClick={()=>{updateSorting({ id:'deadline', desc:!sorting?.desc })}} className="cursor-pointer hover:underline">
                                    <div className="flex items-center gap-2">
                                        {t('Datum deadline')}
                                        <ChevronsUpDownIcon className="w-4 h-4"/>
                                    </div>
                                </TableHead>
                                {getColumns(results)?.map((v, i)=>(
                                        <TableHead onClick={()=>{updateSorting({ id:`metadata[].${v}`, desc:!sorting?.desc })}} key={i} className="cursor-pointer hover:underline">
                                            <div className="flex items-center gap-2">
                                                {metatypeList.find(i=>i.metatype===v)?.metatype_oms?.find(i=>i.nationid===getWindevLang(i18n.language))?.oms || v}
                                                <ChevronsUpDownIcon className="w-4 h-4"/>
                                            </div>
                                        </TableHead>
                                    ))}
                                <TableHead className="text-right"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {results.map((dossier, index)=>(
                            <TableRow key={index} className="hover:bg-blue-100 hover:text-blue-500 group">
                                <TableCell onClick={()=>{handleOpenDossier(dossier.dossierentityid)}} className="cursor-pointer"><img className="w-6 min-w-[24px] h-6" src={folder} alt="folder" /></TableCell>
                                <TableCell onClick={()=>{handleOpenDossier(dossier.dossierentityid)}} className="cursor-pointer">
                                    <div>
                                        <TooltipProvider>
                                            <Tooltip delayDuration={100}>
                                                <TooltipTrigger asChild><p className={`${!dossier.extendeddescription && '!text-neutral-400 italic'} group-hover:text-blue-500 font-medium line-clamp-2`}>{dossier.extendeddescription || t('Ontbreekt')}</p></TooltipTrigger>
                                                <TooltipContent>
                                                    <p>{dossier.extendeddescription || t('Ontbreekt')}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                </TableCell>
                                <TableCell onClick={()=>{handleOpenDossier(dossier.dossierentityid)}} className="cursor-pointer group-hover:text-blue-500">{dossier.documents ? dossier.documents.length : 0}</TableCell>
                                <TableCell onClick={()=>{handleOpenDossier(dossier.dossierentityid)}} className="cursor-pointer">
                                    <p className="group-hover:text-blue-500">{getItemById(dossiertypes, "dossiertypeid", dossier.dossiertypeid)?.name}</p>
                                </TableCell>
                                {/* <TableCell onClick={()=>{handleOpenDossier(dossier.dossierentityid)}} className="cursor-pointer">
                                    <p className="whitespace-nowrap group-hover:text-blue-500">{dossier.state}</p>
                                </TableCell> */}
                                <TableCell onClick={()=>{handleOpenDossier(dossier.dossierentityid)}} className="cursor-pointer">
                                    <p className="whitespace-nowrap group-hover:text-blue-500">{usernameToName(dossier.owner)}</p>
                                </TableCell>
                                <TableCell onClick={()=>{handleOpenDossier(dossier.dossierentityid)}} className="cursor-pointer">
                                    <p className="whitespace-nowrap group-hover:text-blue-500">{dossier.creationdate && isValid(new Date(dossier.creationdate)) && format(new Date(dossier.creationdate), "d MMMM yyyy")}</p>
                                </TableCell>
                                <TableCell onClick={()=>{handleOpenDossier(dossier.dossierentityid)}} className="cursor-pointer">
                                    <p className="whitespace-nowrap group-hover:text-blue-500">{dossier.deadline && isValid(new Date(dossier.deadline)) && format(new Date(dossier.deadline), "d MMMM yyyy")}</p>
                                </TableCell>
                                {getColumns(results)?.map((v, i)=>(
                                    <TableCell key={i}>{dossier?.metadata?.find((md:any) => md?.metatype === v)?.metadata || ''}</TableCell>
                                ))}
                                <TableCell>
                                    <div className="flex items-center justify-end">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger className="flex items-center justify-center w-8 h-8"><MoreVertical className="w-4 h-4 text-neutral-500"/></DropdownMenuTrigger>
                                            <DropdownMenuContent>
                                                <DropdownMenuGroup>
                                                    <DropdownMenuItem onClick={()=>{setshow_delete(true)}} className="text-red-600 cursor-pointer hover:text-red-700">
                                                        <Trash2 className="w-4 h-4 mr-2" strokeWidth={2}/>
                                                        <span className="font-medium">{t('Verwijderen')}</span>
                                                    </DropdownMenuItem>
                                                </DropdownMenuGroup>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </TableCell>
                            </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    }



                    {(dossiers?.length === 0 || !dossiers) &&
                    <div className='px-16 py-8'>
                        <div className='flex flex-col items-center gap-4'>
                            <img className='w-[200px] h-auto' src={empty} alt="Nothing found"/>
                            <div className='flex flex-col items-center justify-center gap-2'>
                                <p className='font-medium text-center'>{t('Geen dossiers gevonden')}!</p>
                                <p className='text-sm text-center text-neutral-600'>{t('Maak dossiers aan in FITdoc')}</p>
                            </div>
                        </div>
                    </div>
                    }
                </div>
            </div>
        </div>
        </>
    )
}

export default Dossiers;